export const termsDoc = {
  effective: "September 2026",
  updated: "September 2026",
  intro: [
    "Welcome to Aflimart.",
    "These Terms & Conditions (“Terms”) govern your access to and use of the Aflimart website, platform, products, services, and related features operated by Aflimart Technologies LTD, operating the Aflimart Digital Market brand (“Aflimart”, “we”, “us”, or “our”).",
    "By accessing or using Aflimart, you agree to be bound by these Terms. If you do not agree with these Terms, you should not use the platform.",
  ],
  sections: [
    {
      title: "ABOUT AFLIMART",
      blocks: [
        { p: "AFLIMART DIGITAL MARKET is a technology-driven digital marketplace designed to connect buyers, sellers, service providers, and logistics partners on one unified platform." },
        { p: "Our platform is intended to enable individuals and businesses to buy and sell products and services conveniently while providing fast, secure, and reliable delivery solutions through our dispatch and logistics network." },
        { p: "Aflimart is designed to support local vendors, expand market access, create employment opportunities, and simplify digital commerce." },
      ],
    },
    {
      title: "ELIGIBILITY",
      blocks: [
        { p: "You must provide accurate information when registering or using Aflimart." },
        { p: "Where applicable, users must meet the minimum legal age or other eligibility requirements necessary to use particular services." },
        { p: "If you are using Aflimart on behalf of a business or organization, you confirm that you have the authority to act on behalf of that business or organization." },
      ],
    },
    {
      title: "USER ACCOUNTS",
      blocks: [
        { p: "Certain Aflimart services may require users to create an account." },
        { p: "You are responsible for:" },
        { bullets: ["Providing accurate and current information", "Keeping your login credentials secure", "Maintaining the confidentiality of your account", "Ensuring that your account is not used without authorization", "Notifying Aflimart promptly of suspected unauthorized access"] },
        { p: "You must not create an account using false information, impersonate another person, or create an account for unlawful purposes." },
        { p: "Aflimart may suspend or restrict accounts where we reasonably believe that an account has been used in violation of these Terms or applicable law." },
      ],
    },
    {
      title: "BUYERS",
      blocks: [
        { p: "Buyers may use Aflimart to browse products and services, communicate where permitted, place orders, make payments, and receive deliveries." },
        { p: "Buyers agree to:" },
        { bullets: ["Provide accurate delivery information", "Review product or service information before ordering", "Make required payments", "Provide reasonable cooperation for delivery", "Use the platform lawfully", "Not misuse the platform or engage in fraudulent activity"] },
        { p: "Product availability, prices, delivery charges, and other information may change before an order is confirmed." },
      ],
    },
    {
      title: "VENDORS AND SERVICE PROVIDERS",
      blocks: [
        { p: "Vendors and service providers are responsible for the products and services they list on Aflimart." },
        { p: "They must:" },
        { bullets: ["Provide accurate business and contact information", "Provide truthful product and service descriptions", "Set accurate prices", "Maintain accurate availability information", "Fulfill accepted orders appropriately", "Comply with applicable laws and regulations", "Provide products and services that are lawful and safe", "Not list prohibited, counterfeit, stolen, fraudulent, or unlawful products or services", "Cooperate with Aflimart verification procedures"] },
        { p: "Aflimart may review, verify, reject, remove, suspend, or restrict listings, accounts, or vendors where necessary to protect users, the platform, or the integrity of the marketplace." },
      ],
    },
    {
      title: "PRODUCT AND SERVICE QUALITY",
      blocks: [
        { p: "Vendors and service providers are responsible for ensuring that their products and services correspond materially with the descriptions, specifications, prices, and other information presented on the platform." },
        { p: "Aflimart may implement verification, rating, review, monitoring, or quality-control systems to improve trust and marketplace standards." },
        { p: "Verification by Aflimart does not necessarily constitute a guarantee of the quality, safety, legality, authenticity, or suitability of every product or service offered by a third party." },
      ],
    },
    {
      title: "ORDERS",
      blocks: [
        { p: "When a buyer places an order, the order may be subject to confirmation by Aflimart, the relevant vendor, or service provider." },
        { p: "An order may be cancelled or rejected where:" },
        { bullets: ["The product or service is unavailable", "The price or listing information contains an obvious error", "Payment cannot be completed or verified", "Fraud or suspicious activity is detected", "Delivery cannot reasonably be completed", "The vendor or service provider cannot fulfill the order", "Required information is missing or inaccurate", "Cancellation is otherwise permitted under applicable law or the applicable service terms"] },
        { p: "Where appropriate, buyers will be informed of cancellations and applicable refund procedures." },
      ],
    },
    {
      title: "PRICING AND PAYMENTS",
      blocks: [
        { p: "Prices displayed on Aflimart should be reviewed before an order is completed." },
        { p: "Depending on the applicable service, additional charges may include delivery charges, service charges, applicable taxes, or other disclosed fees." },
        { p: "Payments may be processed through third-party payment service providers." },
        { p: "Users agree to provide valid payment information and authorize applicable charges relating to confirmed transactions." },
        { p: "Aflimart may not be responsible for delays, failures, or interruptions caused by third-party payment providers, banking systems, telecommunications networks, or circumstances outside our reasonable control." },
      ],
    },
    {
      title: "DELIVERY AND LOGISTICS",
      blocks: [
        { p: "Aflimart may facilitate delivery through dispatch riders and logistics partners." },
        { p: "Delivery times may vary depending on:" },
        { bullets: ["Delivery location", "Product availability", "Vendor preparation time", "Traffic", "Weather", "Public events or restrictions", "Operational conditions", "Other circumstances outside reasonable control"] },
        { p: "Aflimart will seek to provide reliable and transparent delivery services but does not guarantee a particular delivery time unless expressly stated." },
        { p: "Users are responsible for providing accurate delivery information and being reasonably available to receive deliveries." },
      ],
    },
    {
      title: "DISPATCH RIDERS AND LOGISTICS PARTNERS",
      blocks: [
        { p: "Dispatch riders and logistics partners participating in the Aflimart ecosystem may be required to meet verification, conduct, safety, and performance requirements." },
        { p: "They must:" },
        { bullets: ["Provide accurate information", "Follow applicable delivery instructions", "Handle orders responsibly", "Maintain professional conduct", "Respect customer and vendor information", "Follow applicable traffic, transport, safety, and other laws", "Avoid fraudulent or unauthorized activities"] },
        { p: "Aflimart may suspend or terminate access to its logistics systems where necessary." },
      ],
    },
    {
      title: "REVIEWS AND RATINGS",
      blocks: [
        { p: "Aflimart may allow users to submit ratings, reviews, comments, or other feedback." },
        { p: "Reviews and ratings should be honest, relevant, and based on genuine experiences." },
        { p: "Users must not:" },
        { bullets: ["Submit false or misleading reviews", "Manipulate ratings", "Post defamatory, abusive, threatening, discriminatory, or unlawful content", "Use reviews to extort or unfairly pressure another user", "Submit content intended to harm another person's business without a legitimate basis"] },
        { p: "Aflimart may remove content that violates these Terms or applicable law." },
      ],
    },
    {
      title: "PROHIBITED ACTIVITIES",
      blocks: [
        { p: "Users must not use Aflimart to:" },
        { bullets: ["Commit fraud", "Conduct unlawful transactions", "Sell prohibited or illegal products or services", "Sell counterfeit or stolen goods", "Misrepresent products, services, businesses, or identities", "Attempt unauthorized access to the platform", "Introduce malware or malicious code", "Interfere with platform operations", "Scrape or copy platform information without authorization", "Abuse other users", "Circumvent security systems", "Manipulate orders, payments, reviews, or ratings", "Use Aflimart for activities prohibited by applicable law"] },
        { p: "Aflimart may take appropriate action where prohibited activity is detected or reasonably suspected." },
      ],
    },
    {
      title: "INTELLECTUAL PROPERTY",
      blocks: [
        { p: "The Aflimart name, logo, trademarks, website design, software, platform structure, text, graphics, systems, content, and other materials owned or licensed by Aflimart are protected by applicable intellectual property laws." },
        { p: "Except where expressly permitted, users must not copy, reproduce, modify, distribute, sell, license, reverse engineer, or commercially exploit Aflimart's intellectual property." },
        { p: "Vendors and users retain ownership of intellectual property they lawfully own and submit to the platform, subject to the rights and permissions necessary for Aflimart to operate the platform and provide its services." },
      ],
    },
    {
      title: "PRIVACY",
      blocks: [
        { p: "Aflimart's collection and use of personal information is governed by our Privacy Policy." },
        { p: "By using Aflimart, you acknowledge that personal information may be processed as described in the Privacy Policy." },
        { p: "Users should review the Aflimart Privacy Policy for information about data collection, use, storage, security, sharing, and user rights." },
      ],
    },
    {
      title: "PLATFORM AVAILABILITY",
      blocks: [
        { p: "Aflimart aims to provide a reliable platform but does not guarantee that the website or services will always be available, uninterrupted, secure, or error-free." },
        { p: "The platform may occasionally be unavailable because of:" },
        { bullets: ["Maintenance", "Technical problems", "Updates", "Security incidents", "Network failures", "Third-party service interruptions", "Events outside our reasonable control"] },
        { p: "We may modify, suspend, or discontinue features or services as our business develops." },
      ],
    },
    {
      title: "THIRD-PARTY SERVICES",
      blocks: [
        { p: "Aflimart may use or provide access to third-party services including payment providers, logistics partners, technology providers, hosting services, verification services, analytics services, and external websites." },
        { p: "Third-party services may have their own terms and privacy policies." },
        { p: "Aflimart is not responsible for the independent acts, omissions, policies, availability, or performance of third-party services outside our reasonable control." },
      ],
    },
    {
      title: "LIMITATION OF LIABILITY",
      blocks: [
        { p: "To the extent permitted by applicable law, Aflimart will not be liable for indirect, incidental, special, consequential, or similar losses arising from your use of the platform." },
        { p: "Aflimart does not guarantee that every product, service, vendor, buyer, dispatch rider, logistics partner, or other user will meet every expectation or requirement." },
        { p: "Nothing in these Terms is intended to exclude or limit liability that cannot lawfully be excluded or limited under applicable law." },
      ],
    },
    {
      title: "INDEMNITY",
      blocks: [
        { p: "To the extent permitted by applicable law, users agree to be responsible for claims, losses, liabilities, damages, costs, and expenses arising from:" },
        { bullets: ["Their violation of these Terms", "Their unlawful use of the platform", "Fraudulent or unauthorized activity", "Their violation of applicable laws", "Their infringement of another person's rights", "Information or content they submit to the platform"] },
        { p: "This provision is subject to applicable law." },
      ],
    },
    {
      title: "SUSPENSION AND TERMINATION",
      blocks: [
        { p: "Aflimart may suspend, restrict, or terminate access to an account or service where:" },
        { bullets: ["These Terms are violated", "Fraudulent or suspicious activity is detected", "The user provides false information", "The platform is being misused", "The user's conduct creates risk to other users or Aflimart", "Required verification is not completed", "Continued access would violate applicable law"] },
        { p: "Where appropriate, users may also close their accounts subject to outstanding obligations." },
        { p: "Termination does not automatically remove obligations or rights that are intended to continue after termination." },
      ],
    },
    {
      title: "COMPLAINTS AND DISPUTE RESOLUTION",
      blocks: [
        { p: "Aflimart aims to provide an accessible process for users to raise complaints concerning transactions, vendors, services, deliveries, payments, or other platform-related issues." },
        { p: "Users should first contact Aflimart using the contact information provided below and provide sufficient information to enable us to investigate the matter." },
        { p: "We will seek to review and resolve legitimate complaints fairly and reasonably." },
        { p: "Nothing in these Terms prevents a consumer from exercising rights available under applicable Nigerian law." },
      ],
    },
    {
      title: "GOVERNING LAW",
      blocks: [
        { p: "These Terms shall be governed by and interpreted in accordance with the laws of the Federal Republic of Nigeria, subject to applicable mandatory consumer protection and other legal rights." },
      ],
    },
    {
      title: "CHANGES TO THESE TERMS",
      blocks: [
        { p: "Aflimart may update these Terms from time to time as our platform, services, technology, business structure, or legal requirements develop." },
        { p: "When changes are made, the “Last Updated” date will be updated." },
        { p: "Where appropriate, significant changes may be communicated through the website, platform, email, or other reasonable means." },
        { p: "Continued use of Aflimart after applicable changes take effect may constitute acceptance of the updated Terms, subject to applicable law." },
      ],
    },
    {
      title: "CONTACT INFORMATION",
      blocks: [
        { p: "For questions, complaints, or other inquiries regarding these Terms, please contact:" },
        { lines: ["Aflimart Technologies LTD", "Operating Brand: Aflimart Digital Market", "Email: aflimart.tech@gmail.com", "Phone: 07031517181"] },
      ],
    },
    {
      title: "PRE-LAUNCH NOTICE",
      blocks: [
        { p: "Aflimart is currently in its pre-launch/development stage." },
        { p: "Some features, products, services, marketplace functions, vendor systems, payment systems, logistics services, and other platform capabilities described on the website may not yet be fully operational." },
        { p: "Aflimart may introduce services and features progressively as the platform develops." },
        { p: "Nothing on the pre-launch website should be interpreted as a representation that every planned service or feature is currently available." },
      ],
    },
    {
      title: "LEGAL REVIEW",
      blocks: [
        { p: "These Terms & Conditions are intended as a general website terms framework and should be reviewed and finalized by qualified Nigerian legal professionals before Aflimart begins full commercial operations." },
        { p: "Aflimart may update these Terms as its services, technology, business model, partnerships, and applicable legal requirements develop." },
      ],
    },
  ],
};
